import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { StyleSheet, Platform, View, TextInput, Keyboard, ScrollView } from 'react-native';
import { macComposerPasteProps } from '@/lib/macComposerPaste';
import * as Device from 'expo-device';
import { useTheme } from '@/context/ThemeContext';
import { getShadowThemeStyles } from '@/constants/Colors';
import ChatInputCustomParameterButton from '@/components/ChatInput/ChatInputCustomParameterButton';
import ChatInputFilesContainer from '@/components/ChatInput/ChatInputFilesContainer';
import { isNonBarParam } from '@/components/ChatInput/fileParamUtils';
import InlineParamControl from '@/components/ChatInput/InlineParamControl';
import WorkComposerActions from '@/components/ChatInput/WorkComposerActions';
import WorkThinkingPowerButton from '@/components/ChatInput/WorkThinkingPowerButton';
import ChatInputTextarea from '@/components/ChatInput/ChatInputTextarea';
import useResponsiveness from '@/hooks/useResponsiveness';
import useKeyboardShortcuts from '@/hooks/useKeyboardShortcuts';
import ChatInputModelsButton from '@/components/ChatInput/ChatInputModelsButton';
import ChatInputBanners from '@/components/ChatInput/ChatInputBanners';
import { useAuthStore, useUIStore, useModalStore } from '@/store';
import ComposerActionButtons from '@/components/ChatInput/ComposerActionButtons';
import DictateRecordingOverlay from '@/components/ChatInput/DictateRecordingOverlay';
import useDictateState from '@/hooks/useDictateState';
import dictateModeService, { DICTATE_STATES } from '@/lib/dictateMode';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { CHAT_INPUT_STYLE, MAX_MODELS_SELECTABLE } from '@/constants/config';
import { isComposerEnterEvent, shouldComposerSendOnEnter } from '@/constants/utils/composerEnter';
import ChatInputQuoteContainer from '@/components/ChatInput/ChatInputQuoteContainer';
import AgentTagChip from '@/components/ChatInput/AgentTagChip';
import ChatInputMobileMenuButton from '@/components/ChatInput/ChatInputMobileMenuButton';
import ComposerToolsButton from '@/components/ChatInput/ComposerToolsButton';
import { ComposerToolsDropdown } from '@/components/ChatInput/ComposerToolsMenu';
import IOSComposerToolsMenuButton from './IOSComposerToolsMenuButton';
import ChatInputMobileFileUploadButton from '@/components/ChatInput/ChatInputMobileFileUploadButton';
import ClipboardImagePasteBanner from '@/components/ChatInput/ClipboardImagePasteBanner';
import AgentMentionSuggestions from '@/components/ChatInput/AgentMentionSuggestions';
import useAgentMentions from '@/hooks/useAgentMentions';
import { getIOSSafariBubbleBarPadding } from '@/constants/utils/iosSafari';
import { useTooltipAnchor } from '@/hooks/useTooltipAnchor';
import { registerChatInputFocus } from '@/lib/chatInputFocus';

const DEFAULT_HEIGHT = Platform.OS === 'macos' ? 100 : 40;
const XS_HEIGHT = 90;

const createStyles = (colors, themeStyles, inputHeight, filesHeight, isDisabled, isXS, hasText, insets, bubbleBarPadding = 0, isCompact = false) => {

  const stackedLayout = isXS || isCompact;

  const customStyles = {
    inputRow: {
      flexDirection: 'row',
    },
    leftContainer: {
      height: stackedLayout ? 45 : '100%',
    },
    rightContainer: {
      height: stackedLayout ? 45 : '100%',
    },
  };

  return StyleSheet.create({
    outerContainer: {
      width: '100%',
      flexDirection: 'column',
      // Add bubble bar padding for iOS Safari when keyboard is open
      paddingBottom: bubbleBarPadding,
    },
    container: {
      width: '100%',
      paddingBottom: 0,
    },
    borderedContainer: {
      flexDirection: 'column',
      minHeight: 45,
      height: (inputHeight + filesHeight),
      flex: 1,
      borderTopWidth: 1,
      borderTopColor: themeStyles.borderColor,
      backgroundColor: Platform.OS === 'macos' ? colors.solid_color_block : themeStyles.backgroundColor,
      ...(Platform.OS === 'web' ? { backdropFilter: 'blur(18px) saturate(1.8)', WebkitBackdropFilter: 'blur(18px) saturate(1.8)' } : {}),
    },
    inputRow: {
      flexDirection: customStyles.inputRow.flexDirection,
      alignItems: stackedLayout ? 'flex-end' : 'flex-start',
      justifyContent: 'flex-start',
      height: stackedLayout ? 45 : 'auto',
    },
    topContainer: {
      width: '100%',
      minHeight: 45,
      paddingLeft: 5,
    },
    textareaContainer: {
      width: 'auto',
      flex: 1,
      flexShrink: 1,
      minHeight: 45,
      maxHeight: 240,
      height: Math.min(Math.max(inputHeight, 45), 240),
      overflow: 'hidden',
    },
    leftContainer: {
      height: customStyles.leftContainer.height,
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'flex-start',
      paddingTop: 5,
      paddingBottom: 5,
      paddingLeft: 5,
      gap: 6,
      minWidth: 0,
      // In stacked layout the textarea is on its own row, so the left half of the
      // bottom row can take all remaining width. In desktop (non-stacked) layout
      // the textarea shares the row with the params, so cap the params at half
      // the row and let the inner ScrollView handle overflow.
      ...(stackedLayout
        ? { flex: 1 }
        : { flexShrink: 1, maxWidth: '50%', overflow: 'hidden', marginRight: 'auto' }),
    },
    parameterScroll: {
      flexShrink: 1,
      minWidth: 0,
    },
    parameterScrollContent: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: 0,
      paddingRight: 4,
    },
    rightContainer: {
      paddingTop: 5,
      paddingBottom: 5,
      paddingRight: 5,
      height: customStyles.rightContainer.height,
      marginLeft: 'auto',
      flexShrink: 0,
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'center',
      gap: 5,
    },
    filesContainer: {
      //
    },
    agentTagsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
      paddingHorizontal: 12,
      paddingTop: 6,
    },
  });
};

export default function ChatInputFixed({
  placeholder = 'Type a message...',
  value = '',
  banners,
  quote,

  onActionPress,
  onSubmit,
  onChangeText,
  onFocus,
  onBlur,
  composerInputRef,
  commandSuggestions,
  goalBar,
  onCommandKeyPress,

  isInferencing = false,
  isLoading = false,
  isDisabled = false,
  isCancelable = false,
  autoFocus = false,
  isMissingRequiredParameters = false,
  highlightedMissingParameterIds = [],

  parameters,
  onParameterChange,
  
  files,
  onAddFile,
  onRemoveFile,
  onSetFiles,
  selectedButtons,
  onToggleButton,
  selectedModelIds,
  selectedModels,
  onSetSelectedModels,
  selectedAgentIds,
  selectedAgents,
  onSetSelectedAgents,
  selectedParameters,
  onSetQuote,
  onPasteFiles,
  conversationId,
  collectionId,
  hideVoiceMode = false,
  hideTextInput = false,
  hasAllRequiredFiles = true,
  inlineParams = [],
  isCompact = false,
  withModels = true,
  isWorkMode = false,
  workFollowUpMode = false,
  onSendNow,
  onQueue,
  onCancel,
}) {
  const { colors, theme } = useTheme();
  const { isXS } = useResponsiveness();
  const deviceType = Device.deviceType;
  const isMobileOrTablet = deviceType === Device.DeviceType.PHONE || deviceType === Device.DeviceType.TABLET;
  const defaultHeight = isXS ? XS_HEIGHT : DEFAULT_HEIGHT;
  const [inputHeight, setInputHeight] = useState(defaultHeight);
  const [filesHeight, setFilesHeight] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const dictateState = useDictateState();
  const isDictating = dictateState !== DICTATE_STATES.IDLE;
  const hasInlineParamValue = useMemo(() => {
    return inlineParams.some(param => typeof param?.value === 'string' && param.value.trim());
  }, [inlineParams]);
  const localInputRef = useRef(null);
  const inputRef = composerInputRef || localInputRef;
  useEffect(() => registerChatInputFocus(conversationId, () => inputRef.current?.focus()), [conversationId]);
  const modelSelectorAnchorRef = useTooltipAnchor('model-selector');
  const voiceButtonAnchorRef = useTooltipAnchor('voice-button');
  const composerToolsAnchorRef = useTooltipAnchor('composer-tools');
  const composerPopoverAnchorRef = useRef(null);
  const dictateAnchorRef = useTooltipAnchor('dictate-button');
  const themeStyles = getShadowThemeStyles(theme);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const enterToSend = useUIStore(state => state.enterToSend);
  const submitOnEnter = shouldComposerSendOnEnter({
    isXS,
    isPad: Platform.isPad,
    isMobileOrTablet,
    enterToSend,
  });
  const { openModal } = useModalStore(state => state.actions);
  const insets = useSafeAreaInsets();
  const fileInputRef = useRef(null);
  const showingDisclaimer = useMemo(() => {
    return isXS || isMobileOrTablet;
  }, [isXS, isMobileOrTablet]);
  
  // Calculate bubble bar padding for iOS Safari
  const bubbleBarPadding = useMemo(() => {
    if (!isXS && !isMobileOrTablet) return 0;
    return getIOSSafariBubbleBarPadding(isInputFocused);
  }, [isXS, isMobileOrTablet, isInputFocused]);
  
  const styles = useMemo(() => createStyles(
    colors, 
    themeStyles, 
    inputHeight, 
    filesHeight, 
    isDisabled, 
    isXS, 
    value.length > 0,
    insets,
    bubbleBarPadding,
    isCompact
  ), [colors, themeStyles, inputHeight, filesHeight, isDisabled, isXS, value.length, insets, bubbleBarPadding, isCompact]);

  const keyboardShortcutHandlers = useMemo(() => ({
    FOCUS_CHAT_INPUT: () => inputRef.current?.focus()
  }), []);
  useKeyboardShortcuts(keyboardShortcutHandlers);

  useEffect(() => {
    if (!value) {
      if (Platform.OS === 'web' && inputRef.current) {
        inputRef.current.style.height = `${defaultHeight}px`;
      }
      setInputHeight(defaultHeight);
    }
  }, [value, defaultHeight]);

  const handleOnRemoveQuote = useCallback(() => {
    onSetQuote?.('');
  }, [onSetQuote]);

  const handleSendPress = useCallback(() => {
    onActionPress?.();
  }, [onActionPress]);

  // Wrap focus/blur handlers to track input focus for iOS Safari bubble bar padding
  const handleFocus = useCallback((e) => {
    setIsInputFocused(true);
    onFocus?.(e);
  }, [onFocus]);

  const handleBlur = useCallback((e) => {
    setIsInputFocused(false);
    onBlur?.(e);
  }, [onBlur]);

  const handleFilesLayout = useCallback((event) => {
    const { height } = event.nativeEvent.layout;
    setFilesHeight(files.length ? height : 0);
  }, [files.length]);

  const handleKeyPress = (e) => {
    if (onCommandKeyPress?.(e)) return;
    if (Platform.OS === 'web' && isComposerEnterEvent(e)) {
      const shouldSendOnEnter = submitOnEnter;
      if (e.shiftKey || e.metaKey || !shouldSendOnEnter) {
        e.preventDefault();
        const cursorPosition = e.target.selectionStart;
        const newValue = value.slice(0, cursorPosition) + '\n' + value.slice(e.target.selectionEnd);
        onChangeText(newValue);
        setTimeout(() => {
          e.target.selectionStart = e.target.selectionEnd = cursorPosition + 1;
          updateInputHeight(e.target);
        }, 0);
        return;
      }
      
      e.preventDefault();
      onSubmit?.();
    }
  };

  const updateInputHeight = (element) => {
    if (Platform.OS === 'web') {
      if (!element) return;
      
      // First set a small height to get proper scrollHeight calculation
      element.style.height = '0px';
      
      // Get the scroll height and add a small buffer for better UX
      const scrollHeight = element.scrollHeight;
      const newHeight = Math.min(Math.max(isXS ? XS_HEIGHT : DEFAULT_HEIGHT, scrollHeight), 240);
      
      // Set the new height
      element.style.height = `${newHeight}px`;
      setInputHeight(newHeight);
    }
  };

  const handleSend = () => {
    if (onActionPress?.(value) === false) return;

    // Dismiss keyboard on native platforms after sending
    if (Platform.OS !== 'web') {
      Keyboard.dismiss();
    }

    if (value.trim()) {
      const defaultHeight = isXS ? XS_HEIGHT : DEFAULT_HEIGHT;
      if (Platform.OS === 'web') {
        // Reset height after a small delay to ensure state updates have completed
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.style.height = `${defaultHeight}px`;
            setInputHeight(defaultHeight);
          }
        }, 0);
      } else {
        setInputHeight(defaultHeight);
      }
    }
  };

  const handleSubmit = useCallback(() => {
    if (onSubmit?.() === false) return;
    if (Platform.OS !== 'web' && Platform.OS !== 'macos') Keyboard.dismiss();
  }, [onSubmit]);

  const handleContentSizeChange = useCallback((arg) => {
    if (Platform.OS === 'web') {
      if (inputRef.current) {
        updateInputHeight(inputRef.current);
      }
    } else {
      // Handle both direct height values and native event objects
      let measuredHeight = isXS ? XS_HEIGHT : DEFAULT_HEIGHT;
      if (typeof arg === 'number') {
        measuredHeight = arg;
      } else if (arg && arg.nativeEvent && arg.nativeEvent.contentSize) {
        measuredHeight = arg.nativeEvent.contentSize.height || measuredHeight;
      }
      const minHeight = isXS ? XS_HEIGHT : DEFAULT_HEIGHT;
      const newHeight = Math.min(Math.max(minHeight, measuredHeight), 240);
      setTimeout(() => setInputHeight(newHeight), 0);
    }
  }, [isXS]);

  const handleAddUploadingFile = useCallback((uploadingFiles) => {
    const filesToAdd = Array.isArray(uploadingFiles) ? uploadingFiles : [uploadingFiles];
    filesToAdd.forEach((file) => {
      // Use onAddFile instead of onSetFiles to avoid stale closure issues
      // when multiple files are uploaded simultaneously
      onAddFile?.(file);
    });
  }, [onAddFile]);

  const handleAddFile = useCallback((file, tempId, uploadMeta) => {
    onAddFile?.(file, tempId, uploadMeta);
  }, [onAddFile]);

  const modalParams = useMemo(() => ({
    parameters,
    onParameterChange,
    selectedButtons,
    onToggleButton,
    selectedParameters,
    selectedAgentIds,
    selectedModelIds,
    selectedAgents,
    selectedModels,
    files,
    onAddFile: handleAddFile,
    onAddUploadingFile: handleAddUploadingFile,
    conversationId,
  }), [
    parameters,
    onParameterChange,
    selectedButtons,
    onToggleButton,
    selectedParameters,
    selectedAgentIds,
    selectedModelIds,
    selectedAgents,
    selectedModels,
    files,
    handleAddFile,
    handleAddUploadingFile,
    conversationId,
  ]);

  const handleOpenCustomParametersModal = useCallback(() => {
    openModal('ChatInputCustomParametersModal', modalParams);
  }, [openModal, modalParams]);

  /** On native, bar param buttons open the modal (with optional initial param) instead of dropdown. */
  const handleOpenParameterDetailModal = useCallback((initialDetailParam) => {
    openModal('ChatInputCustomParametersModal', { ...modalParams, initialDetailParam });
  }, [openModal, modalParams]);

  // Whether to use a popover dropdown (desktop) vs modal (mobile/XS)
  const useDesktopDropdown = Platform.OS === 'macos' || (Platform.OS === 'web' && !isXS);

  // Handler for opening the unified Composer Tools menu (the + button tray) — mobile only
  const handleOpenComposerToolsMenu = useCallback(() => {
    openModal('ComposerToolsMenu', {
      conversationId,
      collectionId,
      onAddFile: handleAddFile,
      onAddUploadingFile: handleAddUploadingFile,
      onParameterChange,
      onPasteFiles,
      files,
    });
  }, [
    openModal,
    conversationId,
    collectionId,
    handleAddFile,
    handleAddUploadingFile,
    onParameterChange,
    onPasteFiles,
    files,
  ]);

  const handleChangeText = useCallback((text) => {
    onChangeText(text);
    
    // Update height when text changes on web
    if (Platform.OS === 'web' && inputRef.current) {
      updateInputHeight(inputRef.current);
    }
  }, [onChangeText]);

  const handleDictateCancel = useCallback(() => {
    dictateModeService.cancel();
  }, []);

  const handleDictateSend = useCallback(() => {
    dictateModeService.stopRecording();
  }, []);

  const handleDictateTranscript = useCallback((text) => {
    const existing = value.trim();
    const newValue = existing ? `${existing} ${text}` : text;
    onChangeText(newValue);
    inputRef.current?.focus();
  }, [value, onChangeText]);

  const ChatInputComponent = Platform.OS !== 'web' ? TextInput : ChatInputTextarea;

  // @ mentions work in both composer modes (chat + Work). Web renders them
  // through react-mentions inside ChatInputTextarea; native uses the hook.
  const agentMentions = useAgentMentions({
    value,
    onChangeText,
    selectedAgents,
    onSetSelectedAgents,
    enabled: Platform.OS !== 'web',
  });

  const chatInputContainer = (
    <View style={[styles.textareaContainer, { height: Math.min(Math.max(inputHeight, 45), 240) }]}>
      <ChatInputComponent
        {...macComposerPasteProps(onPasteFiles)}
                {...(Platform.OS === 'macos' ? { enableFocusRing: false } : {})}
        value={value}
        onChangeText={handleChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.text_color_tertiary}
        {...(Platform.OS === 'web' ? { inputRef } : { ref: inputRef })}
        {...(Platform.OS !== 'web' ? { 
          multiline: true, 
          scrollEnabled: true, 
          textAlignVertical: 'top',
          submitBehavior: submitOnEnter ? 'submit' : 'newline',
          onSubmitEditing: submitOnEnter ? handleSubmit : undefined,
          ...(Platform.OS === 'macos' ? { submitKeyEvents: [] } : {}),
          onKeyPress: onCommandKeyPress,
          onSelectionChange: agentMentions.onSelectionChange,
          style: {
            color: colors.text_color_primary,
            fontSize: Platform.OS === 'macos' ? 14 : (isWorkMode ? 15 : 17),
            ...(Platform.OS === 'macos' ? { lineHeight: 21 } : isWorkMode ? { lineHeight: 22.5 } : {}),
          },
        } : {})}
        {...(Platform.OS === 'web' ? { onKeyDown: handleKeyPress, isWorkMode } : {})}
        {...(Platform.OS === 'web' ? { onPasteFiles } : {})}
        onFocus={handleFocus}
        onBlur={handleBlur}
        colors={colors}
        theme={theme}
        themeStyles={themeStyles}
        onContentSizeChange={handleContentSizeChange}
        chatInputStyle={CHAT_INPUT_STYLE.FIXED}
        enableAgentMentions
        selectedAgents={selectedAgents}
        onSetSelectedAgents={onSetSelectedAgents}
        autoFocus={autoFocus}
      />
    </View>
  );

  return (
    <View style={styles.outerContainer}>
      <ChatInputBanners banners={banners} />

      <View style={styles.container}>
        {goalBar}
        {!isDictating && !hideTextInput && commandSuggestions}

        {/* Keep the @ picker outside the height-locked borderedContainer. */}
        {agentMentions.isOpen && !isDictating && !hideTextInput && (
          <AgentMentionSuggestions
            suggestions={agentMentions.suggestions}
            isSearching={agentMentions.isSearching}
            onSelect={agentMentions.applyMention}
          />
        )}

        <View ref={composerPopoverAnchorRef} collapsable={false} style={[styles.borderedContainer, Platform.OS === 'macos' && isInputFocused && { borderTopColor: colors.text_color_tertiary }]} id="chat-input-container">
          <ClipboardImagePasteBanner onPasteFiles={onPasteFiles} enabled={Platform.OS !== 'web'} />
          <View style={styles.filesContainer} onLayout={handleFilesLayout}>
            <ChatInputFilesContainer files={files} onDeleteFile={onRemoveFile} parameters={parameters} />
          </View>

          {/* Tagged bot — selection lives here, not inline in the text. */}
          {selectedAgents?.length > 0 && (
            <View style={styles.agentTagsRow}>
              {selectedAgents.map(agent => (
                <AgentTagChip
                  key={agent.id}
                  agent={agent}
                  onRemove={() => onSetSelectedAgents?.([])}
                />
              ))}
            </View>
          )}

          <ChatInputQuoteContainer
            quote={quote} 
            onRemoveQuote={handleOnRemoveQuote} 
          />

          {Array.isArray(inlineParams) && inlineParams.length > 0 && !isDictating && (
            <View style={{ paddingTop: 4, paddingBottom: 2 }}>
              {inlineParams.map((p) => (
                <InlineParamControl
                  key={`inline-param-${p?.id || p?.name}`}
                  param={p}
                  onChange={(next) => onParameterChange?.(p?.id || p?.name, next)}
                />
              ))}
              <View
                style={{
                  height: StyleSheet.hairlineWidth,
                  backgroundColor: themeStyles.borderColor,
                  marginHorizontal: 12,
                  marginTop: 4,
                }}
              />
            </View>
          )}

          {(isXS || isCompact) && !hideTextInput && (
            <View style={[styles.topContainer, isDictating && { display: 'none' }]}>
              {chatInputContainer}
            </View>
          )}

          <View style={styles.inputRow}>
            {isDictating && (
              <DictateRecordingOverlay
                dictateState={dictateState}
                onCancel={handleDictateCancel}
                onSend={handleDictateSend}
              />
            )}

            <View ref={composerToolsAnchorRef} style={[styles.leftContainer, isDictating && { display: 'none' }]}>
              {Platform.OS === 'ios' ? (
                <IOSComposerToolsMenuButton
                  conversationId={conversationId}
                  files={files}
                  onPasteFiles={onPasteFiles}
                  onParameterChange={onParameterChange}
                  onAddFile={handleAddFile}
                  isMissingRequiredParameters={isMissingRequiredParameters}
                />
              ) : useDesktopDropdown ? (
                <ComposerToolsDropdown
                  TriggerComponent={ComposerToolsButton}
                  anchorRef={composerPopoverAnchorRef}
                  triggerComponentProps={{ isMissingRequiredParameters }}
                  conversationId={conversationId}
                  collectionId={collectionId}
                  onAddFile={handleAddFile}
                  onAddUploadingFile={handleAddUploadingFile}
                  onParameterChange={onParameterChange}
                  files={files}
                />
              ) : (
                <ComposerToolsButton
                  onPress={handleOpenComposerToolsMenu}
                  isMissingRequiredParameters={isMissingRequiredParameters}
                />
              )}
              {/* Param chips can outgrow the row when a model exposes lots of
                  options. Scroll them horizontally instead of pushing the
                  textarea / send button off-screen. */}
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                style={styles.parameterScroll}
                contentContainerStyle={styles.parameterScrollContent}
              >
                {Array.isArray(parameters) && parameters
                  .filter(param => param?.id !== 'files' && param?.id !== 'tools' && !isNonBarParam(param))
                  .map((param, index) => (
                    <ChatInputCustomParameterButton 
                      key={`chat-input-custom-parameter-button-${param?.id || index}`}
                      icon={param?.ui?.icon}
                      text={param?.ui?.text || param?.value}
                      parameterData={param}
                      onPress={onParameterChange}
                      isSelected={selectedButtons.includes(param.id)}
                      onToggle={() => onToggleButton?.(param.id)}
                      selectedParameters={selectedParameters}
                      selectedAgentIds={selectedAgentIds}
                      selectedModelIds={selectedModelIds}
                      selectedAgents={selectedAgents}
                      selectedModels={selectedModels}
                      onAddFile={handleAddFile}
                      onAddUploadingFile={handleAddUploadingFile}
                      files={files}
                      isHighlighted={highlightedMissingParameterIds?.includes(param?.id || param?.name)}
                      onOpenDetailModal={handleOpenParameterDetailModal}
                    />
                  ))
                }
              </ScrollView>
            </View>

            {!isXS && !isCompact && !isDictating && !hideTextInput && chatInputContainer}

            <View style={[styles.rightContainer, isWorkMode && { flexShrink: 1, minWidth: 0 }, isDictating && { display: 'none' }]}>
              {withModels && (
                <View ref={(node) => { modelSelectorAnchorRef.current = node; }}>
                  <ChatInputModelsButton
                    isDisabled={isInferencing}
                    onChange={onSetSelectedModels}
                    modelIds={selectedModelIds}
                    shouldCloseOnSelect={true}
                    maxModels={MAX_MODELS_SELECTABLE}
                    inputs={selectedParameters?.action === 'create-image' ? ['text'] : []}
                    outputs={selectedParameters?.action === 'create-image' ? ['image'] : []}
                    selectedModels={selectedModels}
                    dropdownSide={conversationId && conversationId !== 'new' ? 'top' : 'bottom'}
                    isCompact={isCompact}
                  />
                </View>
              )}

              {isWorkMode && (
                <WorkThinkingPowerButton
                  conversationId={conversationId}
                  collectionId={collectionId}
                  compact={isXS || isCompact}
                />
              )}

              {workFollowUpMode ? (
                <WorkComposerActions
                  hasContent={!!value?.trim() || !!files?.length || hasInlineParamValue}
                  onQueue={onQueue}
                  onSendNow={onSendNow}
                  onStop={onCancel}
                  isDisabled={isDisabled}
                  isLoading={isLoading}
                  onTranscript={handleDictateTranscript}
                  dictateAnchorRef={dictateAnchorRef}
                />
              ) : (
                <ComposerActionButtons
                isInferencing={isInferencing}
                isLoading={isLoading}
                isDisabled={isDisabled}
                isCancelable={isCancelable}
                hideTextInput={hideTextInput}
                hideVoiceMode={hideVoiceMode}
                hasAllRequiredFiles={hasAllRequiredFiles}
                value={value}
                hasInlineParamValue={hasInlineParamValue}
                files={files}
                onSend={handleSend}
                onTranscript={handleDictateTranscript}
                conversationId={conversationId}
                dictateAnchorRef={dictateAnchorRef}
                voiceButtonAnchorRef={voiceButtonAnchorRef}
                />
              )}
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}
